import React, { useEffect, useState, useMemo } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { getTextAlign } from '../utils/rtl';
import { COLORS, FONTS } from '../constants/config';
import { DeletionImpact, CascadeImpactItem } from '../types';
import { Ionicons } from '@expo/vector-icons';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
interface DeletionImpactModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: () => void;
  entityName: string;
  fetchImpact: () => Promise<DeletionImpact>;
  isOrgEmployee: boolean;
  isSubmitting?: boolean;
}

export default function DeletionImpactModal({
  visible,
  onClose,
  onConfirm,
  entityName,
  fetchImpact,
  isOrgEmployee,
  isSubmitting = false,
}: DeletionImpactModalProps) {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const [impact, setImpact] = useState<DeletionImpact | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      loadImpact();
    } else {
      setImpact(null);
      setError(null);
    }
  }, [visible]);

  const loadImpact = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchImpact();
      setImpact(data);
    } catch (err) {
      setError(t('messages.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const getTypeLabel = (type: string) => {
    return t(`deletion.${type}`, type);
  };

  const renderChildren = (
    allItems: CascadeImpactItem[],
    parentType: string,
    parentId: number,
    depth: number,
  ) => {
    const children = allItems.filter(
      (c) => c.parent_type === parentType && c.parent_id === parentId,
    );
    if (children.length === 0) return null;

    return children.map((child, index) => (
      <View key={`${child.type}-${child.id}`}>
        <View style={[styles.treeItem, { [isRTL ? 'marginRight' : 'marginLeft']: depth * 20 }]}>
          <Text style={styles.treeBranch}>
            {index === children.length - 1 ? '└── ' : '├── '}
          </Text>
          <Text style={[styles.treeItemText, isRTL && styles.rtlText]}>
            {child.name} ({getTypeLabel(child.type)})
          </Text>
        </View>
        {renderChildren(allItems, child.type, child.id, depth + 1)}
      </View>
    ));
  };

  const renderTree = (items: CascadeImpactItem[]) => {
    const rootItems = items.filter((i) => i.is_root);

    return (
      <View style={styles.treeContainer}>
        {rootItems.map((root) => (
          <View key={`${root.type}-${root.id}`}>
            <View style={[styles.treeItem, styles.rootItem]}>
              <Ionicons name="trash-outline" size={16} color={tokens.colors.raw.red["500"]} />
              <Text style={[styles.treeItemText, styles.rootItemText, isRTL && styles.rtlText]}>
                {root.name} ({getTypeLabel(root.type)})
              </Text>
            </View>
            {renderChildren(items, root.type, root.id, 1)}
          </View>
        ))}
      </View>
    );
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          <View style={styles.header}>
            <Ionicons name="warning" size={24} color={tokens.colors.raw.yellow["500"]} />
            <Text style={[styles.headerTitle, isRTL && styles.rtlText]}>
              {t('deletion.impactTitle')}
            </Text>
          </View>

          <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
            {loading ? (
              <ActivityIndicator size="large" color={COLORS.primary} />
            ) : error ? (
              <Text style={styles.errorText}>{error}</Text>
            ) : impact ? (
              <>
                <Text style={[styles.description, isRTL && styles.rtlText]}>
                  {t('deletion.impactDescription')}
                </Text>

                {renderTree(impact.items)}

                {impact.unlinked_kpis.length > 0 && (
                  <View style={styles.warningBox}>
                    <Ionicons name="link-outline" size={16} color={tokens.colors.raw.yellow["500"]} />
                    <Text style={[styles.warningText, isRTL && styles.rtlText]}>
                      {t('deletion.unlinkedKpis', { count: impact.unlinked_kpis.length })}
                    </Text>
                  </View>
                )}

                <View style={styles.summaryBox}>
                  <Text style={[styles.summaryText, isRTL && styles.rtlText]}>
                    {t('deletion.totalAffected', { count: impact.total_affected })}
                  </Text>
                </View>
              </>
            ) : null}
          </ScrollView>

          <View style={[styles.actions, isRTL && styles.actionsRTL]}>
            <Button variant="secondary" onPress={onClose} disabled={isSubmitting}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onPress={onConfirm}
              loading={isSubmitting}
              disabled={loading || !!error}
            >
              {isOrgEmployee ? t('deletion.submitRequest') : t('common.delete')}
            </Button>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacing.absolute["5"],
  },
  modalContainer: {
    backgroundColor: '#fff',
    borderRadius: tokens.radii.absolute["radius-16"],
    width: '100%',
    maxWidth: 500,
    maxHeight: '80%',
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: tokens.spacing.absolute["5"],
    paddingBottom: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["2_5"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: '#E5E5E5',
  },
  headerTitle: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    fontFamily: FONTS.semiBold,
    color: '#171717',
  },
  content: {
    maxHeight: 400,
  },
  contentContainer: {
    padding: tokens.spacing.absolute["5"],
  },
  description: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: '#525252',
    marginBottom: tokens.spacing.absolute["4"],
    fontFamily: FONTS.regular,
  },
  treeContainer: {
    marginBottom: tokens.spacing.absolute["4"],
  },
  treeItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: tokens.spacing.absolute["1"],
    gap: tokens.spacing.absolute["1_5"],
  },
  rootItem: {
    paddingVertical: tokens.spacing.absolute["2"],
  },
  treeItemText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: '#171717',
    fontFamily: FONTS.regular,
  },
  rootItemText: {
    fontFamily: FONTS.semiBold,
  },
  treeBranch: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: '#A3A3A3',
    fontFamily: 'monospace',
  },
  warningBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
    backgroundColor: '#FEF9C3',
    padding: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-8"],
    marginBottom: tokens.spacing.absolute["3"],
  },
  warningText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    color: '#854D0E',
    fontFamily: FONTS.regular,
    flex: 1,
  },
  summaryBox: {
    backgroundColor: '#F5F5F5',
    padding: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-8"],
  },
  summaryText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.semiBold,
    color: '#171717',
    marginBottom: tokens.spacing.absolute["1"],
  },
  restoreRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
    marginTop: tokens.spacing.absolute["1"],
  },
  restoreText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    color: '#16A34A',
    fontFamily: FONTS.regular,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["3"],
    borderTopWidth: tokens.spacing.absolute["px"],
    borderTopColor: '#E5E5E5',
  },
  actionsRTL: {
    flexDirection: 'row-reverse',
  },
  errorText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.red["500"],
    textAlign: 'center',
  },
  rtlText: {
    textAlign: getTextAlign(isRTL),
  },
});

const styles = getStyles(true);
